import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../../shadcn/ui/dialog";
import { Input } from "../../../shadcn/ui/input";
import { Label } from "../../../shadcn/ui/label";
import { Button } from "../../../shadcn/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../shadcn/ui/card";
import { Badge } from "../../../shadcn/ui/badge";
import { FaPercent, FaEdit, FaInfoCircle } from "react-icons/fa";
import billingService from "../../../services/billingService";
import { showApiMessage } from "../../../utils/messageHandler";
import logger from "../../../utils/logger";
import LoadingSpinner from "../../../components/LoadingSpinner";

const RevenueShareManagement = () => {
  const [defaultCommission, setDefaultCommission] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editCommission, setEditCommission] = useState(0);
  const [lastModified, setLastModified] = useState(null);

  useEffect(() => {
    loadDefaultRevenueShare();
  }, []);

  const loadDefaultRevenueShare = async () => {
    setLoading(true);
    try {
      const response = await billingService.getDefaultRevenueShare();
      if (response.status === 200) {
        const data = response.data?.data || response.data;
        // Convert decimal to percentage for display (multiply by 100)
        const commissionDecimal = data.commissionPercentage || 0;
        setDefaultCommission(commissionDecimal * 100);
        setLastModified(data.modifiedAt || data.createdAt);
      }
    } catch (err) {
      logger.error("Error loading default revenue share:", err);
      // If no default is set, show 0
      setDefaultCommission(0);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = () => {
    setEditCommission(defaultCommission);
    setShowEditDialog(true);
  };

  const handleSaveDefault = async () => {
    if (editCommission < 0 || editCommission > 100) {
      showApiMessage(
        { response: { data: { message: "Commission percentage must be between 0 and 100" } } },
        "validation error"
      );
      return;
    }

    setSaving(true);
    try {
      // Convert percentage back to decimal for API (divide by 100)
      const commissionDecimal = parseFloat(editCommission) / 100;
      const response = await billingService.setDefaultRevenueShare({
        commissionPercentage: commissionDecimal,
      });

      if (response.status === 200) {
        showApiMessage(response, "update default revenue share");
        setDefaultCommission(parseFloat(editCommission));
        setLastModified(new Date().toISOString());
        setShowEditDialog(false);
      }
    } catch (err) {
      showApiMessage(err, "update default revenue share");
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditCommission(defaultCommission);
    setShowEditDialog(false);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner message="Loading revenue share settings..." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold mb-2">Revenue Share Management</h2>
        <p className="text-gray-600">
          Manage the default revenue share percentage for all organizations. Individual organizations can have custom
          rates set separately.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FaPercent className="h-5 w-5 text-blue-600" />
            Default Revenue Share
          </CardTitle>
          <CardDescription>
            This is the default commission percentage applied to all organizations unless they have a custom rate set.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-2xl font-bold">{defaultCommission}%</span>
                <Badge variant="secondary" className="bg-blue-100 text-blue-800">
                  Default Rate
                </Badge>
              </div>
              {lastModified && (
                <p className="text-sm text-gray-500">
                  Last modified: {new Date(lastModified).toLocaleDateString()} at{" "}
                  {new Date(lastModified).toLocaleTimeString()}
                </p>
              )}
            </div>
            <Button onClick={handleEditClick} className="flex items-center gap-2">
              <FaEdit className="h-4 w-4" />
              Edit Default Rate
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FaInfoCircle className="h-5 w-5 text-green-600" />
            How Revenue Share Works
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 text-sm text-gray-600">
            <p>
              • <strong>Default Rate:</strong> Applied to all organizations that don't have a custom rate set
            </p>
            <p>
              • <strong>Custom Rates:</strong> Individual organizations can have their own commission percentage
            </p>
            <p>
              • <strong>Override:</strong> Custom rates always take precedence over the default rate
            </p>
            <p>
              • <strong>Reset:</strong> Organizations can be reset to use the default rate by removing their custom rate
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Edit Default Revenue Share</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="default-commission">Default Commission Percentage (%)</Label>
              <Input
                id="default-commission"
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={editCommission}
                onChange={e => setEditCommission(e.target.value)}
                placeholder="Enter default commission percentage"
              />
              <p className="text-xs text-gray-500">
                Enter a value between 0 and 100. This will be the default rate for all organizations.
              </p>
            </div>
          </div>
          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={handleCancelEdit} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSaveDefault} disabled={saving}>
              {saving ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RevenueShareManagement;
